import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { dateWithZerosUTC } from '../../../../util';
import { ClickAnchor, ClickAnchorProps } from '../../ClickMenu';
import { elementsInputClasses, InputContainer, InputContainerProps } from '../Input';
import { DatePickerFloatingMenu } from './components';
import { DatePickerContextProvider } from './datePicker.context';
import { useDatePicker, UseDatePickerArgs } from './hooks';
import { ClientOnly } from 'remix-utils/client-only';
import { Icon } from '../../Icon';

export interface DatePickerProps
  extends UseDatePickerArgs,
    Omit<InputContainerProps, 'isReadOnly' | 'children' | 'trailing'>,
    Pick<ClickAnchorProps, 'placement'> {
  name?: string;
  placeholder?: string;
  withIcon?: boolean;
  onBlur?: ComponentPropsWithoutRef<'input'>['onBlur'];
  offsetYears?: number;
  inPortal?: boolean;
}

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      offsetYears,
      name,
      placeholder,
      status,
      onBlur,
      onChange,
      value,
      withIcon = true,
      inPortal = false,
      placement,
      ...inputContainerProps
    },
    ref
  ) => {
    const datePickerData = useDatePicker({ value, onChange });

    return (
      <ClickAnchor
        offset={[0, 5]}
        inPortal={inPortal}
        placement={placement || 'bottom-end'}
        disabled={status === 'disabled'}
        floatingElement={
          <DatePickerContextProvider {...datePickerData}>
            <ClientOnly>{() => <DatePickerFloatingMenu offsetYears={offsetYears} />}</ClientOnly>
          </DatePickerContextProvider>
        }
      >
        {({ setOpen }) => (
          <InputContainer
            status={status}
            {...inputContainerProps}
            trailing={withIcon && <Icon icon="calendarToDay" className="text-palette-600 ElementsInput-trailing" />}
          >
            <div className="relative">
              {datePickerData.chosenDate && (
                <input
                  readOnly
                  ref={ref}
                  id={name}
                  name={name}
                  type="hidden"
                  disabled={status === 'disabled'}
                  value={dateWithZerosUTC(datePickerData.chosenDate)}
                />
              )}
              <input
                type="text"
                onBlur={onBlur}
                placeholder={placeholder}
                value={datePickerData.userInput}
                disabled={status === 'disabled'}
                className={elementsInputClasses.input}
                onChange={(e) => datePickerData.setUserInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setOpen(false);
                  }
                }}
              />
            </div>
          </InputContainer>
        )}
      </ClickAnchor>
    );
  }
);
